import { useEffect, useState } from 'react'

import { isContentfulPreviewFrame } from '../helpers/isContentfulPreviewFrame'
import {
  ContentfulPreviewAsset,
  ContentfulPreviewEntry,
  ContentfulPreviewPayload,
  fetchContentfulPreviewEntries,
} from '../services/contentfulPreviewService'

// Stable references so the live preview SDK does not see a new subscription on every render
// while there is nothing to overlay yet.
const NO_ENTRIES: ContentfulPreviewEntry[] = []
const NO_ASSETS: ContentfulPreviewAsset[] = []
const NO_PREVIEW_DATA: ContentfulPreviewPayload = { entries: NO_ENTRIES, assets: NO_ASSETS }

/**
 * Loads the draft version of a page's entries (and linked assets) from Contentful, once,
 * in the preview iframe.
 *
 * Returns empty lists everywhere else, and also when the fetch fails, which leaves callers
 * on their build-time data rather than on a broken page.
 */
export const useContentfulPreviewEntries = (pageContentfulId: string): ContentfulPreviewPayload => {
  const [previewData, setPreviewData] = useState<ContentfulPreviewPayload>(NO_PREVIEW_DATA)

  useEffect(() => {
    if (!pageContentfulId || !isContentfulPreviewFrame()) return

    let isCurrent = true

    fetchContentfulPreviewEntries(pageContentfulId)
      .then(fetched => {
        if (isCurrent) setPreviewData(fetched)
      })
      .catch(error => {
        console.error('Falling back to build time content.', error)
        if (isCurrent) {
          setPreviewData(current => (current === NO_PREVIEW_DATA ? current : NO_PREVIEW_DATA))
        }
      })

    return () => {
      isCurrent = false
    }
  }, [pageContentfulId])

  return previewData
}
